import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
    children?: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
    }

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="h-full flex flex-col items-center justify-center p-6 text-center space-y-4">
                    <div className="p-4 bg-danger/10 rounded-full">
                        <AlertTriangle className="w-8 h-8 text-danger" />
                    </div>
                    <h2 className="text-xl font-bold text-fg">Something went wrong</h2>
                    <p className="text-fg-3 max-w-md">
                        {this.state.error?.message || 'An unexpected error occurred while loading this component.'}
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="flex items-center gap-2 px-4 py-2 fd-btn-primary font-bold rounded-lg transition-all"
                    >
                        <RefreshCw className="w-4 h-4" />
                        RELOAD PAGE
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}
